import {
  FormHelperText,
  List,
  ListItem,
  ListItemButton,
  ListSubheader,
  hexToRgb,
  Box,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ContentState, convertToRaw, EditorState, Modifier } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { FunctionComponent, useState, useEffect } from 'react';
import { Editor } from 'react-draft-wysiwyg';

import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import FullScreenLoader from '@Components/FullScreenLoader';

import './textEditor.scss';
import TextEditorProps, {
  PlaceHolderOptions,
  PlaceHolderProps,
} from './TextEditor.types';

const PlaceHolders: FunctionComponent<PlaceHolderProps> = ({
  editorState,
  placeholderOptions,
  onChangeEditorState,
}) => {
  const [placeHolderState, setPlaceholderState] = useState({ open: false });
  const theme = useTheme();

  const openPlaceholderDropdown = () => {
    setPlaceholderState({ open: !placeHolderState.open });
  };

  const addPlaceholder = (placeholder: string) => {
    const contentState = Modifier.replaceText(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      placeholder,
      editorState
        .getCurrentInlineStyle()
        .add(`color-${hexToRgb(theme.palette.secondary.main)}`)
    );
    onChangeEditorState(
      EditorState.push(editorState, contentState, 'insert-characters')
    );

    setPlaceholderState({ open: false });
  };

  const groupedPlaceHolderOptions = placeholderOptions?.reduce(
    (result: { [key: string]: PlaceHolderOptions[] }, item) => {
      const temp: PlaceHolderOptions[] = result[item.group] || [];
      temp.push(item);
      result[item.group] = temp;
      return result;
    },
    {}
  );

  const getNewList = () => {
    const groupList: JSX.Element[] = [];
    // eslint-disable-next-line
    for (const list in groupedPlaceHolderOptions) {
      const newGroup = (
        <ListItem key={list} sx={{ p: 0, m: 0 }}>
          <List
            className={`rdw-dropdown-optionwrapper ${
              placeHolderState.open ? '' : 'placeholder-ul'
            }`}
            sx={{ overflow: 'auto' }}
          >
            <ListSubheader sx={{ px: 1, py: 0, margin: 0 }}>
              {list}
            </ListSubheader>
            {groupedPlaceHolderOptions[list].map((item) => (
              <ListItem
                key={item.name}
                sx={{ padding: 0, margin: 0, width: '100%' }}
              >
                <ListItemButton
                  sx={{ color: 'secondary.main' }}
                  color="primary"
                  tabIndex={0}
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                  onKeyDown={() => {
                    addPlaceholder(`{{${item.name}}}`);
                  }}
                  onClick={() => {
                    addPlaceholder(`{{${item.name}}}`);
                  }}
                  key={item.name}
                  className="rdw-dropdownoption-default placeholder-li"
                >
                  #{item.displayName}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </ListItem>
      );
      groupList.push(newGroup);
    }
    return groupList;
  };

  return (
    <Box
      role="button"
      onClick={openPlaceholderDropdown}
      onKeyDown={openPlaceholderDropdown}
      className="rdw-block-wrapper"
      aria-label="rdw-block-control"
    >
      <Box
        className="rdw-dropdown-wrapper rdw-block-dropdown"
        aria-label="rdw-dropdown"
        minWidth="18.2rem"
      >
        <Box className="rdw-dropdown-selectedtext" title="Placeholders">
          <Box color="secondary.main" component="span">
            Add Tag {placeHolderState.open}
          </Box>
          <Box
            className={`rdw-dropdown-caretto${
              placeHolderState.open ? 'close' : 'open'
            }`}
            sx={{
              borderTopColor: 'secondary.main',
              borderBottomColor: 'secondary.main',
            }}
          />
        </Box>
        <List
          className={`rdw-dropdown-optionwrapper ${
            placeHolderState.open ? '' : 'placeholder-ul'
          }`}
          sx={{
            position: 'relative',
            overflow: 'auto',
            maxHeight: 300,
            '& ul': { padding: 0 },
          }}
          subheader={<li />}
        >
          {placeholderOptions.length ? (
            getNewList()
          ) : (
            <FullScreenLoader sx={{ my: 1 }} LoaderProps={{ size: '3rem' }} />
          )}
        </List>
      </Box>
    </Box>
  );
};

const TextEditor: FunctionComponent<TextEditorProps> = (
  props: TextEditorProps
): JSX.Element => {
  const {
    value,
    placeholder,
    error,
    errorText,
    readOnly,
    onChange,
    placeholderOptions,
  } = props;
  const theme = useTheme();

  /** This updated state keeps track of user interaction */
  const [updated, setUpdated] = useState(false);

  /** Creates the initial state for text editor
   * @description Takes in the HTML text and returns the initial state
   * @param text HTML string to pre-populate the text editor
   * @returns {EditorState} The inital state for Text Editor
   */
  const createState = (text: string | undefined) => {
    if (text) {
      const blocksFromHTML = htmlToDraft(text);
      const state = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
      );
      return EditorState.createWithContent(state);
    }
    return EditorState.createEmpty();
  };

  const [editorState, setEditorState] = useState<EditorState>(
    createState(value)
  );

  /** To change editorState value if value is changed outside
   * the component
   */
  useEffect(() => {
    if (!updated) {
      setEditorState(createState(value));
    }
  }, [value, updated]);

  /**
   * Controls the onchange for the text-editor
   * @param state The editor state object
   */
  const onEditorStateChange = (state: EditorState) => {
    setUpdated(true);

    const newState = EditorState.setInlineStyleOverride(
      state,
      state
        .getCurrentInlineStyle()
        .remove(`color-${hexToRgb(theme.palette.secondary.main)}`)
    );
    setEditorState(newState);

    if (onChange) {
      onChange(draftToHtml(convertToRaw(state.getCurrentContent())));
    }
  };

  return (
    <Box id="text-editor" className="editor">
      <Editor
        placeholder={placeholder}
        editorState={editorState}
        readOnly={readOnly}
        onEditorStateChange={onEditorStateChange}
        wrapperClassName={error ? 'wrapper-class error' : 'wrapper-class'} // class applied around both the editor and the toolbar
        editorClassName="editor-class" // class applied around the editor
        toolbarClassName="toolbar-class" // class applied around the toolbar
        toolbarCustomButtons={[
          <Box position="relative">
            <PlaceHolders
              placeholderOptions={placeholderOptions}
              editorState={editorState}
              onChangeEditorState={setEditorState}
            />
            {readOnly && (
              <Box
                position="absolute"
                top="0"
                left="0"
                zIndex="1200"
                height="100%"
                width="100%"
              />
            )}
          </Box>,
        ]}
        toolbar={{
          options: ['history', 'inline', 'list', 'colorPicker'],
          inline: {
            inDropdown: false,
            className: 'toolbar__inline',
            options: ['bold', 'italic', 'underline'],
          },
          list: {
            inDropdown: false,
            className: 'toolbar_list',
            options: ['unordered', 'ordered'],
          },
        }}
      />
      {error && (
        <FormHelperText
          error
          sx={{ position: 'absolute', bottom: '-1.9rem', mx: 1.4 }}
        >
          {errorText}
        </FormHelperText>
      )}
    </Box>
  );
};

export default TextEditor;
